import { Assignment, ChevronLeft, ChevronRight, DirectionsCar, ExpandMore, Payments, Search, Visibility } from "@mui/icons-material";
import { Box, Chip, Collapse, IconButton, InputAdornment, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { QrCode } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorScreen, Loading } from "../../../components";
import { useToastStore } from "../../../stores";
import { colorPrimary, colorSubText, textFieldStyle } from "../../../theme";
import { formatDateFromISOTable } from "../../../utils/mask/MaskUtils";
import { AnuncioOfertadosShopkeeperDto, DashBoardMinhasCompras } from "../dto/ShopkeeperDTO";
import { fetchMinhasCompras, fetchMinhasComprasDashBoard, fetchQrCodeAnuncio } from "../repository/ShopkeeperRepository";
import { QrCodeModal } from "./QrCodeModal";
import { StatCard } from "./StatCard";

export function MinhasComprasTab() {
    const navigate = useNavigate();
    const theme = useTheme();
    const { setToast } = useToastStore(); 
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const itemsPerPage = 10;
    const [minhasOfertas, setMinhasOfertas] = useState<AnuncioOfertadosShopkeeperDto[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(false);
    const [dashBoard, setDashBoard] = useState<DashBoardMinhasCompras>();
    const jaCarregou = useRef(false);
  // Filtrar ofertas baseado no termo de busca
  const filteredOfertas = minhasOfertas.filter((compra) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    // Busca no status
    if (compra.status.toLowerCase().includes(searchLower)) return true;
    
    // Busca nos veículos
    return compra.veiculos.some((veiculo) =>
      veiculo.marca.toLowerCase().includes(searchLower) ||
      veiculo.modelo.toLowerCase().includes(searchLower)
    );
  });
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };


  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };
  
  const fetchMinhasComprasTable = useCallback(async () => {
    try {
      const result = await fetchMinhasCompras();
      if(!result?.success) {
        setToast({variant:"error", message:result.message});
        setError(true);
        return;
      }
      setMinhasOfertas(result.data);
    } catch (error) {
      setToast({variant:"error", message:"Erro. Contate o administrador."});
      setError(true);
    } finally {
      setIsLoading(false);
    }
    }, [setToast]);

    
    const fetchDashBoard = useCallback(async () => {
        try {
          const result = await fetchMinhasComprasDashBoard();
          if(!result?.success) {
            setToast({variant:"error", message:result.message});
            setError(true);
            return;
          }
          setDashBoard(result.data);
        } catch (error) {
          setToast({variant:"error", message:"Erro. Contate o administrador."});
          setError(true);
        } finally {
          setIsLoading(false);
        }
        }, [setToast]);


  // Calcular paginação
  const totalPages = Math.ceil(filteredOfertas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOfertas = filteredOfertas.slice(startIndex, endIndex);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [qrCode, setQrCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  
  const handleOpenQrCodeModal = async (id_anuncio: number) => {
    const qrCodeData = await fetchQrCode(id_anuncio);
    if (qrCodeData) {
      setQrCode(qrCodeData);
      setQrCodeModalOpen(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };
  
  const fetchQrCode = useCallback(async (id_anuncio: number) => {
    try {
      setIsLoading(true);
      const result = await fetchQrCodeAnuncio(id_anuncio);
      if(!result?.success) {
        setToast({variant:"error", message:result.message});
        return null;
      }
      return result.data;
    } catch (error) {
      setToast({variant:"error", message:"Erro. Contate o administrador."});
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [setToast]);

  const handleCloseQrCodeModal = () => {
    setQrCodeModalOpen(false);
  };
  

  // Resetar página quando mudar o filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(()=>{
    const loadData = async () => {
      if(jaCarregou.current) return;
      jaCarregou.current = true;
      setIsLoading(true);
      await fetchDashBoard();
      await fetchMinhasComprasTable();
      setIsLoading(false);
    };
    loadData();
  }, [fetchDashBoard, fetchMinhasComprasTable]);

  if(isLoading) return <Loading  fullScreen={true}/>
  if(error) return <ErrorScreen fullScreen={true}/>

  
    return (
        <>
    {/* Modal QR Code */}
      <QrCodeModal
        open={qrCodeModalOpen}
        onClose={handleCloseQrCodeModal}
        qrCodeData={qrCode}
        title="QR Code de Pagamento Pix"
      />
        <Box display={"flex"} flexDirection={"column"} gap={2}>
        {/* Stats Cards */}
        <Box
          display="grid"
          gridTemplateColumns={
            isMobile
              ? "1fr"
              : isTablet
              ? "repeat(2, 1fr)"
              : "repeat(4, 1fr)"
          }
          gap={2}
          mb={4}
        >
          <StatCard
            value={dashBoard?.qtd_comprada ?? 0}
            label="Comprados"
            icon={<DirectionsCar sx={{ fontSize: 20, color: "white" }} />}
            colorMain="rgb(255, 106, 0)"
            colorBg="rgba(255, 107, 0, 0.1)"
          />

          <StatCard
            value={dashBoard?.qtd_proposta ?? 0}
            label="Propostas"
            icon={<Visibility sx={{ fontSize: 20, color: "white" }} />}
            colorMain="rgb(5, 150, 104)"
            colorBg="rgba(5, 150, 105, 0.1)"
          />

          <StatCard
            value={dashBoard?.qtd_pagamento_aberto ?? 0}
            label="Pendentes"
            icon={<Payments sx={{ fontSize: 20, color: "white" }} />}
            colorMain="rgb(220, 38, 38)"
            colorBg="rgba(220, 38, 38, 0.1)"
          />

          <StatCard
            value={dashBoard?.qtd_cotacao ?? 0}
            label="Cotações"
            icon={<Assignment sx={{ fontSize: 20, color: "white" }} />}
            colorMain="rgb(37, 100, 235)"
            colorBg="rgba(37, 99, 235, 0.1)"
          />
        </Box>
        <Box
          bgcolor="white"
          borderRadius={3}
          border="1px solid #e2e8f0"
          overflow="hidden"
          sx={{ boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
        >
          {/* Header with Search */}
          <Box
            px={isMobile ? 3 : 4}
            py={3}
            borderBottom="1px solid #f1f5f9"
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "stretch" : "center"}
            gap={2}
          >
            <Typography fontSize="1.325rem" fontWeight={700}>
              Minhas Compras/Ofertas
            </Typography>
            <TextField
              size="small"
              placeholder="Buscar veículo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={textFieldStyle}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
             
            />
          </Box>

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(248, 250, 252, 0.5)" }}>
                  <TableCell
                    sx={{
                      width: "45%",
                      fontSize: "0.825rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      py: 2,
                    }}
                  >
                    Veículo
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: "15%",
                      fontSize: "0.825rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      py: 2,
                    }}
                  >
                    Data
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: "15%",
                      fontSize: "0.825rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      py: 2,
                    }}
                  >
                    Valor
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: "15%",
                       fontSize: "0.825rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      py: 2,
                    }}
                  >
                    Situação
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: "10%",
                       fontSize: "0.825rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      py: 2,
                    }}
                  >
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOfertas.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        fontSize="1rem"
                        fontWeight={500}
                        color={colorSubText}
                      >
                        {searchTerm
                          ? "Nenhum resultado encontrado para sua busca."
                          : "Você ainda não possui compras ou ofertas."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedOfertas.map((compra, index) => (
                  <React.Fragment key={compra.id_anuncio}>
                    <TableRow
                      sx={{
                        bgcolor:
                          index % 2 === 1
                            ? "rgba(248, 250, 252, 0.5)"
                            : "transparent",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          bgcolor: "rgba(248, 250, 252, 0.8)",
                        },
                      }}
                    >
                      {/* Veículo Column */}
                      <TableCell sx={{ py: 3, borderBottom: "1px solid #f1f5f9" }}>
                        <Box display="flex" alignItems="flex-start" gap={2}>
                          {/* Icon/Image */}
                          <Box
                            width={56}
                            height={56}
                            bgcolor="#f1f5f9"
                            borderRadius={2}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            position="relative"
                            overflow="hidden"
                          >
                            <DirectionsCar
                              sx={{ fontSize: 24, color: "#94a3b8" }}
                            />
                            {compra.veiculos.length > 1 && (
                              <Box
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="grid"
                                gridTemplateColumns="1fr 1fr"
                                gap="1px"
                                sx={{ opacity: 0.2 }}
                              >
                                <Box bgcolor="#94a3b8" />
                                <Box bgcolor="#64748b" />
                              </Box>
                            )}
                          </Box>

                          {/* Info */}
                          <Box flex={1}>
                            <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                              {compra.veiculos.length > 1 && (
                                <Chip
                                  label="COMBO"
                                  size="small"
                                  sx={{
                                    bgcolor: colorPrimary,
                                    color: "white",
                                    fontSize: "0.5rem",
                                    fontWeight: 900,
                                    height: 18,
                                    textTransform: "uppercase",
                                    fontStyle: "italic",
                                    "& .MuiChip-label": {
                                      px: 0.75,
                                    },
                                  }}
                                />
                              )}
                              <Typography
                                fontWeight={700}
                                fontSize="0.875rem"
                                color="#1e293b"
                              >
                                {compra.veiculos[0].marca + " " + compra.veiculos[0].modelo}
                                {compra.veiculos.length > 1 && (
                                  <Typography
                                    component="span"
                                    fontWeight={500}
                                    fontSize="0.8rem"
                                    color="#64748b"
                                  >
                                    {" "}+ {compra.veiculos.length - 1} veículo{compra.veiculos.length - 1 > 1 ? "s" : ""}
                                  </Typography>
                                )}
                              </Typography>
                            </Stack>

                            {compra.veiculos.length > 1 && (
                              <Typography
                                fontSize="0.6875rem"
                                color="#94a3b8"
                                mb={1}
                              >
                                {compra.veiculos?.length} Veículos no pacote
                              </Typography>
                            )}

                            {compra.veiculos.length === 1 && compra.veiculos[0].marca && compra.veiculos[0].modelo && (
                              <Typography
                                fontSize="0.6875rem"
                                  color="#94a3b8"
                                  fontWeight={500}
                                >
                                  {compra.veiculos[0].marca + " " + compra.veiculos[0].modelo} • {compra.veiculos[0].placa}
                                </Typography>
                            )}

                            {compra.veiculos.length > 1 && (
                              <Box mt={1}>
                                <Box
                                  display="inline-flex"
                                  alignItems="center"
                                  gap={0.5}
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => toggleRowExpansion(compra.id_anuncio)}
                                >
                                  <Typography
                                    fontSize="0.625rem"
                                    fontWeight={700}
                                    color={colorPrimary}
                                    textTransform="uppercase"
                                    letterSpacing={0.5}
                                    sx={{
                                      "&:hover": {
                                        color: "#ea580c",
                                      },
                                    }}
                                  >
                                    Detalhes
                                  </Typography>
                                  <ExpandMore
                                    sx={{
                                      fontSize: 14,
                                      color: colorPrimary,
                                      transform: expandedRows.includes(compra.id_anuncio)
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.3s",
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Data Column */}
                      <TableCell
                        align="center"
                        sx={{ py: 3, borderBottom: "1px solid #f1f5f9" }}
                      >
                        <Typography
                          fontSize="0.95rem"
                          fontWeight={500}
                          color="#64748b"
                        >
                          {formatDateFromISOTable(compra.data_cadastro)}
                        </Typography>
                      </TableCell>

                      {/* Valor Column */}
                      <TableCell
                        align="center"
                        sx={{ py: 3, borderBottom: "1px solid #f1f5f9" }}
                      >
                        <Typography
                          fontSize="0.975rem"
                          fontWeight={900}
                          color="#1e293b"
                        >
                          {formatCurrency(compra.val_proposta)}
                        </Typography>
                      </TableCell>

                      {/* Situação Column */}
                      <TableCell
                        align="center"
                        sx={{ py: 3, borderBottom: "1px solid #f1f5f9" }}
                      >
                        <Stack alignItems="center" gap={0.75}>
                          {/* Status Principal */}
                          <Chip
                            label={compra.status}
                            size="small"
                            sx={{
                              bgcolor: compra.status === "Comprado" 
                                ? "#dcfce7" 
                                : compra.status === "Ofertado" 
                                ? "#ffedd5" 
                                : "#f1f5f9",
                              color: compra.status === "Comprado"
                                ? "#15803d"
                                : compra.status === "Ofertado"
                                ? "#c2410c"
                                : "#475569",
                              fontSize: "0.5625rem",
                              fontWeight: 700,
                              height: 24,
                              textTransform: "uppercase",
                              letterSpacing: 1,
                              minWidth: 90,
                              "& .MuiChip-label": {
                                px: 1.25,
                              },
                            }}
                          />
                          
                          {/* Badge Pendente ou Destaque */}
                          {compra.pendente !== null ? (
                            <Chip
                              label={compra.pendente}
                              size="small"
                              sx={{
                                bgcolor: "#fee2e2",
                                color: "#b91c1c",
                                fontSize: "0.5625rem",
                                fontWeight: 700,
                                height: 24,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                                minWidth: 90,
                                "& .MuiChip-label": {
                                  px: 1.25,
                                },
                              }}
                            />
                          ) : (
                            compra.status === "Ofertado" && (
                              <Chip
                                label="Em andamento"
                                size="small"
                                sx={{
                                  bgcolor: "#dbeafe",
                                  color: "#1e40af",
                                  fontSize: "0.5625rem",
                                  fontWeight: 700,
                                  height: 24,
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                  minWidth: 90,
                                  "& .MuiChip-label": {
                                    px: 1.25,
                                  },
                                }}
                              />
                            )
                          )}
                        </Stack>
                      </TableCell>

                      {/* Ações Column */}
                      <TableCell
                        align="left"
                        sx={{ py: 3, borderBottom: "1px solid #f1f5f9" }}
                      >
                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                          <IconButton
                            size="small"
                            onClick={ () => { 
                              if(compra.veiculos.length >1) {
                                navigate(`/anuncios/detalhe/combo/${compra.id_anuncio}`);
                              }
                              else {
                                navigate(`/anuncios/detalhe/${compra.id_anuncio}`);
                              }
                              }}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "#1e293b",
                              color: "white",
                              borderRadius: 1.5,
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              "&:hover": {
                                bgcolor: colorPrimary,
                              },
                            }}
                          >
                            <Visibility sx={{ fontSize: 18 }} />
                          </IconButton>
                          {compra.pendente !== null ? (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenQrCodeModal(compra.id_anuncio)}
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "#059669",
                                color: "white",
                                borderRadius: 1.5,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                "&:hover": {
                                  bgcolor: "#047857",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                              title="Ver QR Code de Pagamento"
                            >
                              <QrCode  />
                            </IconButton>
                          ) : (
                            <Box width={32} height={32} />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row Details */}
                    {compra.veiculos.length > 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{
                            py: 0,
                            borderBottom: "1px solid #f1f5f9",
                            bgcolor:
                              index % 2 === 1
                                ? "rgba(248, 250, 252, 0.5)"
                                : "transparent",
                          }}
                        >
                          <Collapse
                            in={expandedRows.includes(compra.id_anuncio)}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box py={2} pl={9} pr={4}>
                              <Stack
                                spacing={1}
                                borderLeft={`2px solid ${colorPrimary}33`}
                                pl={2}
                              >
                                {compra.veiculos?.map((veiculo) => (
                                  <Box
                                    key={veiculo.id ?? veiculo.marca + " " + veiculo.modelo}
                                    bgcolor="white"
                                    p={1.5}
                                    borderRadius={1.5}
                                    border="1px solid #f1f5f9"
                                    sx={{
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                    }}
                                  >
                                    <Typography
                                      fontSize="0.825rem"
                                      fontWeight={700}
                                      mb={0.5}
                                    >
                                      {veiculo.marca + " " + veiculo.modelo + " • " + veiculo.placa}
                                    </Typography>
                                    <Typography
                                      fontSize="0.7625rem"
                                      color="#64748b"
                                    >
                                      {veiculo.ano_fabricacao} / {veiculo.ano_modelo} • {veiculo.km} km •{" "}
                                      {veiculo.transmissao} • {veiculo.placa}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer with Pagination */}
          <Box
            px={4}
            py={2}
            borderTop="1px solid #f1f5f9"
            bgcolor="rgba(248, 250, 252, 0.3)"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
          >
            <Typography
              fontSize="0.625rem"
              fontWeight={500}
              color="#94a3b8"
              textTransform="uppercase"
              letterSpacing={1}
            >
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredOfertas.length)} de {filteredOfertas.length} resultados
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  color: currentPage === 1 ? "#cbd5e1" : "#94a3b8",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  "&:hover": {
                    bgcolor: currentPage === 1 ? "transparent" : "white",
                  },
                  "&:disabled": {
                    color: "#cbd5e1",
                  },
                }}
              >
                <ChevronLeft sx={{ fontSize: 18 }} />
              </IconButton>
              <Box
                width={32}
                height={32}
                bgcolor={colorPrimary}
                color="white"
                borderRadius={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography fontSize="0.75rem" fontWeight={700}>
                  {currentPage}
                </Typography>
              </Box>
              {totalPages > 1 && (
                <Typography
                  fontSize="0.75rem"
                  fontWeight={500}
                  color="#94a3b8"
                  display="flex"
                  alignItems="center"
                  px={1}
                >
                  de {totalPages}
                </Typography>
              )}
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  color: currentPage === totalPages ? "#cbd5e1" : "#94a3b8",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  "&:hover": {
                    bgcolor: currentPage === totalPages ? "transparent" : "white",
                  },
                  "&:disabled": {
                    color: "#cbd5e1",
                  },
                }}
              >
                <ChevronRight sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Box>
        </Box>
        </Box>
        </>
    );
}